import { useState, useCallback, useMemo, useEffect } from 'react';
import { MRT_SortingState } from 'material-react-table';
import { useTableValidation, createRequiredValidator } from './useTableValidation';

// Common types used throughout the application
export interface PaginationState {
  pageIndex: number;
  pageSize: number;
}

export interface TableSortingState extends MRT_SortingState {}

export interface TableRequestParams {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc'; // Restricted to these specific values
  searchQuery?: string;
}

/**
 * Enhanced table hook that combines pagination, sorting, and validation
 * Reduces duplication by providing a single source for table state management
 */
export function useEnhancedTable<T extends Record<string, any>>(options: {
  initialPageIndex?: number;
  initialPageSize?: number;
  initialSorting?: MRT_SortingState;
  onSearchQueryChange?: string;
  requiredFields?: Array<keyof T>;
}) {
  const { initialPageIndex = 0, initialPageSize = 100, initialSorting = [], onSearchQueryChange, requiredFields = [] } = options;

  // =========== Pagination State ===========
  const [pagination, setPagination] = useState({
    pageIndex: initialPageIndex,
    pageSize: initialPageSize,
  });

  // =========== Sorting State ===========
  const [sorting, setSorting] = useState<MRT_SortingState>(initialSorting);

  // =========== Validation State ===========
  const { validationErrors, setValidationErrors, getTextFieldProps, validateItem, clearValidationErrors } = useTableValidation<T>(
    createRequiredValidator<T>(requiredFields as Array<keyof T>)
  );

  // =========== Pagination Handlers ===========
  // Reset pagination to first page
  const resetPagination = useCallback(() => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  }, []);

  // Reset to page 1 when search query changes
  useEffect(() => {
    if (onSearchQueryChange !== undefined) {
      resetPagination();
    }
  }, [onSearchQueryChange, resetPagination]);

  // Handle page change (1-based to 0-based conversion)
  const onChangePage = useCallback((page: number) => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: page - 1,
    }));
  }, []);

  // Handle page size change
  const onChangePageSize = useCallback((newPageSize: number) => {
    setPagination((prev) => ({
      ...prev,
      pageSize: newPageSize,
      pageIndex: 0, // Reset to first page when changing page size
    }));
  }, []);

  // =========== Sorting Handlers ===========
  // Handle sorting change with pagination reset
  const handleSortingChange = useCallback(
    (updatedSorting: MRT_SortingState) => {
      resetPagination();
      setSorting(updatedSorting);
    },
    [resetPagination]
  );

  // =========== API Request Helpers ===========
  // Helper to prepare common request params
  const getRequestParams = useCallback((): TableRequestParams => {
    // Extract sorting information if available
    const sortInfo =
      sorting.length > 0
        ? {
            sortBy: sorting[0].id,
            sortOrder: sorting[0].desc ? ('desc' as const) : ('asc' as const),
          }
        : {};

    // Build the request params
    return {
      page: pagination.pageIndex,
      pageSize: pagination.pageSize,
      ...sortInfo,
      ...(onSearchQueryChange ? { searchQuery: onSearchQueryChange } : {}),
    };
  }, [pagination, sorting, onSearchQueryChange]);

  // =========== Table State Summary ===========
  // Combine all table state in a single object for easy access
  const tableState = useMemo(
    () => ({
      pagination,
      sorting,
      isValid: Object.keys(validationErrors).length === 0,
    }),
    [pagination, sorting, validationErrors]
  );

  return {
    // State
    pagination,
    sorting,
    validationErrors,
    tableState,

    // Setters
    setPagination,
    setSorting,
    setValidationErrors,

    // Handlers
    onChangePage,
    onChangePageSize,
    handleSortingChange,
    resetPagination,

    // Validation
    getTextFieldProps,
    validateItem,
    clearValidationErrors,

    // Helpers
    getRequestParams,
  };
}
